import { Outlet, useMatch, useSearchParams, useParams } from "react-router-dom";
import { Affix, message } from "antd";
import { SimpleBusinessSearch } from "./search";
import { Header } from "./head";
import { Footer } from "./footer";
import styles from "./style.module.scss";
import { useCallback, useEffect, useRef, useState } from "react";
import { isInIframe } from "@/utils";
import {
  createSupplierTrackEvent,
  getMemberCompanyInfo,
  createSubscribeRuleDebug,
  getSupplierTrackMemberLevel,
} from "@/services";
import {
  cancelSubscribeCorporation,
  closeOpportunityDetailGuide,
  getOpportunityDetailGuideCloseStatus,
} from "@/services/business";
import { useBusinessInfo } from "@/hooks";
import { BusinessCompanyInfoType } from "@/types";
import { useCurrent } from "@/providers";

export const SimpleBusinessLayout = () => {
  const [companyInfo, setCompanyInfo] = useState<BusinessCompanyInfoType>();
  const [showBusinessUpgradeAd, setShowBusinessUpgradeAd] = useState(false);
  const [showGuestLoginCard, setShowGuestLoginCard] = useState(true);
  const [showGuestLoginBanner, setShowGuestLoginBanner] = useState(true);
  const guestPopupExposureKeyRef = useRef<string>();
  const guestBottomBarExposureKeyRef = useRef<string>();
  const businessPopupExposureKeyRef = useRef<string>();
  const {
    isLogin,
    userInfoIsFetched,
    memberQuotaBaseInfo,
    memberQuotaBaseIsFetched,
  } = useCurrent();
  const businessDetailRouteMatch = useMatch("/go/detail/:opportunityId");
  const [searchParams] = useSearchParams();
  const memberId = searchParams.get("memberId");
  const companyName = searchParams.get("companyName");
  const groupId = searchParams.get("groupId");
  const companyId = searchParams.get("companyId");
  // opportunityId：商机id   
  const { opportunityId } = useParams();

  const isCaCertified =
    memberQuotaBaseInfo?.caMemberTypeCode === "FADADA_CA_CERTIFIED";
  const showCaUpgradeAd =false;

  const showGuestGuides =
    !!businessDetailRouteMatch && !!userInfoIsFetched && !isLogin;
  const memberLevel = getSupplierTrackMemberLevel(memberQuotaBaseInfo);

  const trackPopup = useCallback(
    (
      slotId:
        | "adv_bizdetail_popup"
        | "login_bizdetail_popup"
        | "login_bizdetail_bottombar",
      eventName: "guide_expose" | "guide_click" | "guide_close",
      eventType: "EXPOSE" | "CLICK" | "CLOSE",
      elementName: string,
      closeType?: "close" | "close_today",
    ) => {
      createSupplierTrackEvent({
        eventId: `evt_${crypto.randomUUID()}`,
        eventName,
        eventType,
        eventTime: Date.now(),
        slotId,
        memberLevel,
        inOpenList: memberQuotaBaseInfo?.caOpenListHit === true,
        device: "pc",
        pageName: "opportunity_detail",
        moduleName:
          slotId === "login_bizdetail_bottombar"
            ? "bottom_bar"
            : "right_popup",
        elementName,
        closeType,
        properties: {},
      }).catch(() => undefined);
    },
    [memberLevel, memberQuotaBaseInfo?.caOpenListHit],
  );

  const handleLogin = () => {
    window.location.href = `${process.env.LOGIN_URL}?gotoUrl=${encodeURIComponent(
      window.location.href,
    )}`;
  };

  const handleGuestPopupLogin = () => {
    trackPopup(
      "login_bizdetail_popup",
      "guide_click",
      "CLICK",
      "login_register",
    );
    handleLogin();
  };

  const handleGuestBottomBarLogin = () => {
    trackPopup(
      "login_bizdetail_bottombar",
      "guide_click",
      "CLICK",
      "login_register",
    );
    handleLogin();
  };

  const closeGuestLoginCard = () => {
    trackPopup(
      "login_bizdetail_popup",
      "guide_close",
      "CLOSE",
      "close_button",
      "close",
    );
    setShowGuestLoginCard(false);
  };

  useEffect(() => {
    if (!showGuestGuides) return;
    setShowGuestLoginCard(true);
    setShowGuestLoginBanner(true);
  }, [showGuestGuides, opportunityId]);

  const { businessInfo } = useBusinessInfo();

  useEffect(() => {
    if (
      !businessDetailRouteMatch ||
      !memberQuotaBaseIsFetched ||
      !isLogin ||
      memberQuotaBaseInfo?.memberTypeCode === "PREMIUM_PACKAGE"
    ) {
      setShowBusinessUpgradeAd(false);
      return;
    }

    let active = true;
    getOpportunityDetailGuideCloseStatus()
      .then((closedToday) => {
        if (active) setShowBusinessUpgradeAd(closedToday !== true);
      })
      .catch(() => {
        if (active) setShowBusinessUpgradeAd(false);
      });

    return () => {
      active = false;
    };
  }, [
    businessDetailRouteMatch,
    isLogin,
    memberQuotaBaseInfo?.memberTypeCode,
    memberQuotaBaseIsFetched,
  ]);

  useEffect(() => {
    if (!showGuestGuides || !showGuestLoginCard) return;
    const exposureKey = opportunityId || "unknown";
    if (guestPopupExposureKeyRef.current === exposureKey) return;
    guestPopupExposureKeyRef.current = exposureKey;
    trackPopup(
      "login_bizdetail_popup",
      "guide_expose",
      "EXPOSE",
      "popup",
    );
  }, [opportunityId, showGuestGuides, showGuestLoginCard, trackPopup]);

  useEffect(() => {
    if (!showGuestGuides || !showGuestLoginBanner) return;
    const exposureKey = opportunityId || "unknown";
    if (guestBottomBarExposureKeyRef.current === exposureKey) return;
    guestBottomBarExposureKeyRef.current = exposureKey;
    trackPopup(
      "login_bizdetail_bottombar",
      "guide_expose",
      "EXPOSE",
      "bottom_bar",
    );
  }, [opportunityId, showGuestGuides, showGuestLoginBanner, trackPopup]);

  useEffect(() => {
    if (!showBusinessUpgradeAd) return;
    const exposureKey = opportunityId || "unknown";
    if (businessPopupExposureKeyRef.current === exposureKey) return;
    businessPopupExposureKeyRef.current = exposureKey;
    trackPopup(
      "adv_bizdetail_popup",
      "guide_expose",
      "EXPOSE",
      "popup",
    );
  }, [opportunityId, showBusinessUpgradeAd, trackPopup]);

  const closeBusinessUpgradeAd = async () => {
    trackPopup(
      "adv_bizdetail_popup",
      "guide_close",
      "CLOSE",
      "close_button",
      "close",
    );
    await closeOpportunityDetailGuide();
    setShowBusinessUpgradeAd(false);
  };

  const handleBusinessUpgradeClick = (productId: 1 | 106) => {
    trackPopup(
      "adv_bizdetail_popup",
      "guide_click",
      "CLICK",
      "open_service",
    );
    window.open(
      productId === 1 ? "/become-supplier" : "/caIntroduction",
      "_blank",
    );
  };


  useEffect(() => {
    if(companyInfo) return;
    if((!memberId && !companyName) && !businessInfo[opportunityId!]) return;
    fetchMemberCompanyInfo();
  }, [memberId, businessInfo[opportunityId!], opportunityId, companyInfo]);

  const fetchMemberCompanyInfo= async () => {
    const params = {
      memberId: memberId || undefined,
      companyName: companyName || undefined,
      companyId: companyId || undefined,
    }
    if(opportunityId && businessInfo[opportunityId!]){
      params.companyName = businessInfo[opportunityId!].companyName;
      params.memberId = businessInfo[opportunityId!].memberId;
    }
    try {
      const res = await getMemberCompanyInfo(params);
      setCompanyInfo(res);
    } catch (error) {}
  };


  const handleSubscribe = async (item: any) => {
    try {
      const params = {
        type: "CORPORATION" as const,
        buyerMemberId: item.memberId || "",
      };
      if (item?.subscribe) {
        await cancelSubscribeCorporation({
          buyerMemberId: item.memberId || "",
        });
        message.success("取消订阅成功");
      } else {
        await createSubscribeRuleDebug(params);
        message.success("订阅成功");
      }
      await fetchMemberCompanyInfo();
    } catch (error: any) {
      console.log(error, "error");
      // message.error("订阅失败，请稍后重试");
    }
  };

  return (
    <div className={styles.globalWrapper}>
      <Affix style={{ zIndex: 1000 }}>
        <div>
          {!isInIframe() && <Header noTabs />}
          <SimpleBusinessSearch
            companyInfo={companyInfo}
            type={"business"}
            value={{
              companyId:companyId || companyInfo?.companyId,
              memberId:memberId || companyInfo?.memberId,
            }}
            isGroup={!!companyInfo?.groupId && !!companyInfo?.groupName}
            handleSubscribe={handleSubscribe}
          />
        </div>
      </Affix>
      <div className={styles.outLayoutWrapper}>
        <div className={styles.layoutWrapper}>
          <div className={styles.layoutContent}>
            <Outlet context={{ companyInfo, fetchSupplierInfo: fetchMemberCompanyInfo }} />
          </div>
        </div>
      </div>
      {showGuestGuides && showGuestLoginCard ? (
        <aside
          className={`${styles.businessUpgradeAd} ${styles.businessGuestLoginAd}`}
        >
          <div className={styles.businessGuestLoginAdTopBar}>
            <span>大企汇 · 海量商机</span>
            <button
              type="button"
              aria-label="关闭"
              className={styles.businessGuestLoginAdClose}
              onClick={closeGuestLoginCard}
            >
              ×
            </button>
          </div>
          <div className={styles.businessGuestLoginAdBody}>
            <div className={styles.businessGuestLoginAdTitle}>
              登录即可查看超10万条优质商机
            </div>
            <div className={styles.businessGuestLoginAdDescription}>
              海量真实招标采购项目，尽在大企汇。登录后即可查看完整商机、一键参与报价。
            </div>
            <button
              type="button"
              className={styles.businessGuestLoginAdButton}
              onClick={handleGuestPopupLogin}
            >
              立即登录 / 注册
            </button>
            <div className={styles.businessGuestLoginAdHint}>
              登录后可查看本商机的采购清单与联系方式
            </div>
          </div>
        </aside>
      ) : showBusinessUpgradeAd ? (
        <aside
          className={`${styles.businessUpgradeAd} ${
            showCaUpgradeAd ? styles.businessCaUpgradeAd : ""
          } ${isCaCertified ? styles.businessCertifiedUpgradeAd : ""}`}
        >
          {isCaCertified ? (
            <>
              <div className={styles.businessCertifiedUpgradeAdTopBar}>
                <span>大企汇 · 高级企采商</span>
                <button
                  type="button"
                  aria-label="关闭"
                  className={styles.businessCertifiedUpgradeAdClose}
                  onClick={closeBusinessUpgradeAd}
                >
                  ×
                </button>
              </div>
              <div className={styles.businessCertifiedUpgradeAdBody}>
                <div className={styles.businessCertifiedUpgradeAdTitle}>
                  开通高级企采商，商机报价不限次
                </div>
                <div className={styles.businessCertifiedUpgradeAdDescription}>
                  本商机可报价，但当前免费版报价权益已用完。开通后可报价全站商机，并解锁商机联系方式不限次查看。
                </div>
                <div className={styles.businessCertifiedUpgradeAdBenefits}>
                  <span>✓ 商机报价不限次</span>
                  <span>✓ 商机联系方式不限次查看</span>
                  <span>✓ 采购企业主动触达</span>
                </div>
                <button
                  type="button"
                  className={styles.businessCertifiedUpgradeAdButton}
                  onClick={() => handleBusinessUpgradeClick(1)}
                >
                  开通高级企采商
                </button>
              </div>
            </>
          ) : (
            <>
              <div className={styles.businessUpgradeAdHeader}>
                <button
                  type="button"
                  aria-label="关闭"
                  className={styles.businessUpgradeAdClose}
                  onClick={closeBusinessUpgradeAd}
                >
                  ×
                </button>
                <div className={styles.businessUpgradeAdTitle}>
                  {showCaUpgradeAd
                    ? "开通CA认证供应商，展示企业身份标签"
                    : "升级高级企采商，享受报价不限次权益"}
                </div>
                <div className={styles.businessUpgradeAdSubtitle}>
                  {showCaUpgradeAd
                    ? "身份标签全年展示，买家一眼识别"
                    : "开通高级企采商，全年商机报价不限次"}
                </div>
              </div>
              <div className={styles.businessUpgradeAdBody}>
                <div className={styles.businessUpgradeAdBenefits}>
                  <span className={styles.businessUpgradeAdBadge}>权益</span>
                  {showCaUpgradeAd ? (
                    <>
                      <div>✓ 双CA证书在线签约，资质合规有保障</div>
                      <div>✓ 电子签章即时可用，成交流程更高效</div>
                    </>
                  ) : (
                    <>
                      <div>✓ 商机报价不限次，商机联系方式不限次查看</div>
                      <div>✓ 采购企业主动触达，报价进展可查</div>
                    </>
                  )}
                </div>
                <div className={styles.businessUpgradeAdSlogan}>
                  {showCaUpgradeAd ? (
                    <>让采购商更快识别贵司资质</>
                  ) : (
                    <>
                      不错过每一条
                      <br />
                      匹配商机
                    </>
                  )}
                </div>
                <button
                  type="button"
                  className={styles.businessUpgradeAdButton}
                  onClick={() =>
                    handleBusinessUpgradeClick(showCaUpgradeAd ? 106 : 1)
                  }
                >
                  {showCaUpgradeAd ? "开通CA认证供应商" : "开通高级企采商"}
                </button>
              </div>
            </>
          )}
        </aside>
      ) : null}
      {showGuestGuides && showGuestLoginBanner && (
        <aside className={styles.businessGuestLoginBanner}>
          <div className={styles.businessGuestLoginBannerContent}>
            <span className={styles.businessGuestLoginBannerBrand}>大企汇</span>
            <span className={styles.businessGuestLoginBannerDivider} />
            <strong>登录即可查看超10万条优质商机</strong>
            <span>✓ 海量商机内容</span>
            <span>✓ 收货地址与联系方式</span>
            <span>✓ 一键参与报价</span>
          </div>
          <button
            type="button"
            className={styles.businessGuestLoginBannerButton}
            onClick={handleGuestBottomBarLogin}
          >
            立即登录 / 注册
          </button>
          <button
            type="button"
            aria-label="关闭"
            className={styles.businessGuestLoginBannerClose}
            onClick={() => setShowGuestLoginBanner(false)}
          >
            ×
          </button>
        </aside>
      )}
      <div style={{ width: 1200, margin: "0 auto" }}>
        {!isInIframe() && <Footer />}
      </div>
    </div>
  );
};
